import { useEffect, useState } from "react";
import { Card, Button, Form, Col } from "react-bootstrap";
import { Notyf } from "notyf";

function CartItemCard({ item, fetchCartItems }) {
  const notyf = new Notyf();
  const [loading, setLoading] = useState(false);
  const [oldQuantity, setOldQuantity] = useState(item.quantity);
  const [quantity, setQuantity] = useState(item.quantity);

  const removeFromCart = async (itemId) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/cart/${itemId}/remove-from-cart`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ productId: itemId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to remove item from cart");
      }

      // Refresh cart items after removal
      await fetchCartItems();
      notyf.success("Item removed from cart successfully");
    } catch (error) {
      console.error("Error removing item from cart:", error);
      notyf.error("Error removing item from cart");
    }
    setLoading(false);
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/cart/update-cart-quantity`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            productId: itemId,
            newQuantity: parseInt(newQuantity),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update quantity");
      }

      // Refresh cart items after update
      await fetchCartItems();
      notyf.success("Quantity updated successfully");
    } catch (error) {
      console.error("Error updating quantity:", error);
      notyf.error("Error updating quantity");
    }
    setLoading(false);
  };
  return (
    <Col xs={12} md={6} lg={4} key={item.id} className="mb-4">
      <Card
        className="h-100 cart-item-card border-0 shadow-lg"
        style={{
          borderRadius: "20px",
          overflow: "hidden",
          transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
        }}
      >
        {/* Product Header with Modern Gradient */}
        <div
          className="card-header border-0 position-relative"
          style={{
            background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
            padding: "1.5rem",
          }}
        >
          {/* Pattern Overlay */}
          <div
            className="position-absolute w-100 h-100"
            style={{
              top: 0,
              left: 0,
              background:
                'url("data:image/svg+xml,<svg xmlns=\\"http://www.w3.org/2000/svg\\" viewBox=\\"0 0 100 100\\"><defs><pattern id=\\"cartItemPattern\\" width=\\"20\\" height=\\"20\\" patternUnits=\\"userSpaceOnUse\\"><circle cx=\\"10\\" cy=\\"10\\" r=\\"1\\" fill=\\"white\\" opacity=\\"0.1\\"/></pattern></defs><rect width=\\"100\\" height=\\"100\\" fill=\\"url(%23cartItemPattern)\\"/></svg>")',
              opacity: 0.3,
            }}
          ></div>

          <div className="d-flex align-items-center text-white position-relative">
            <div
              className="me-3"
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "12px",
                width: "45px",
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(10px)",
              }}
            >
              <i className="fas fa-leaf" style={{ fontSize: "1.3rem" }}></i>
            </div>
            <div>
              <h5 className="mb-1 fw-bold">{item.productId.name}</h5>
              <small className="opacity-75">Premium Quality</small>
            </div>
          </div>
        </div>

        <Card.Body className="p-4">
          {/* Product Description */}
          <div className="mb-4">
            <p
              className="text-muted mb-0"
              style={{ fontSize: "0.9rem", lineHeight: "1.5" }}
            >
              {item.productId.description}
            </p>
          </div>

          {/* Price Information */}
          <div className="price-section mb-4">
            <div className="row g-3">
              <div className="col-6">
                <div
                  className="text-center p-3"
                  style={{
                    background: "rgba(40, 167, 69, 0.1)",
                    borderRadius: "12px",
                  }}
                >
                  <small className="text-muted d-block mb-1">Unit Price</small>
                  <span className="fw-bold text-success">
                    ₱
                    {parseFloat(item.productId.price).toLocaleString("en-PH", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
              <div className="col-6">
                <div
                  className="text-center p-3"
                  style={{
                    background: "rgba(40, 167, 69, 0.1)",
                    borderRadius: "12px",
                  }}
                >
                  <small className="text-muted d-block mb-1">Subtotal</small>
                  <span className="fw-bold text-success">
                    ₱
                    {parseFloat(item.subtotal).toLocaleString("en-PH", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Modern Quantity Control */}
          <div className="quantity-section mb-4">
            <Form.Label className="fw-semibold text-dark mb-3 d-block">
              <i className="fas fa-cubes me-2 text-success"></i>
              Quantity
            </Form.Label>
            <div className="d-flex align-items-center justify-content-center">
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => {
                  if (quantity > 1) {
                    const newQty = quantity - 1;
                    setQuantity(newQty);
                    updateQuantity(item.productId._id, newQty);
                    setOldQuantity(newQty);
                  }
                }}
                disabled={loading || quantity <= 1}
                className="quantity-btn"
                style={{
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fas fa-minus"></i>
              </Button>

              <div
                className="mx-3 text-center"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  borderRadius: "12px",
                  padding: "10px 20px",
                  minWidth: "80px",
                }}
              >
                <span
                  className="fw-bold text-dark"
                  style={{ fontSize: "1.2rem" }}
                >
                  {quantity}
                </span>
                <small className="d-block text-muted">kg</small>
              </div>

              <Button
                variant="outline-success"
                size="sm"
                onClick={() => {
                  const newQty = quantity + 1;
                  setQuantity(newQty);
                  updateQuantity(item.productId._id, newQty);
                  setOldQuantity(newQty);
                }}
                disabled={loading}
                className="quantity-btn"
                style={{
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fas fa-plus"></i>
              </Button>
            </div>
          </div>

          {/* Remove Button */}
          <div className="d-grid">
            <Button
              onClick={() => removeFromCart(item.productId._id)}
              variant="outline-danger"
              size="sm"
              disabled={loading}
              className="fw-semibold"
              style={{
                borderRadius: "12px",
                padding: "12px",
                transition: "all 0.3s ease",
              }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Loading...
                </>
              ) : (
                <>
                  <i className="fas fa-trash me-2"></i>
                  Remove from Cart
                </>
              )}
            </Button>
          </div>
        </Card.Body>

        {/* Enhanced Styles */}
        <style jsx>{`
          .cart-item-card {
            transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          }

          .cart-item-card:hover {
            transform: translateY(-8px) scale(1.02);
            box-shadow: 0 20px 40px rgba(40, 167, 69, 0.15) !important;
          }

          .quantity-btn {
            transition: all 0.2s ease;
          }

          .quantity-btn:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
          }

          .quantity-btn:disabled {
            opacity: 0.5;
            transform: none;
          }

          .btn-outline-danger:hover {
            background-color: #dc3545 !important;
            border-color: #dc3545 !important;
            color: white !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
          }

          @media (max-width: 768px) {
            .cart-item-card:hover {
              transform: translateY(-4px);
            }
          }
        `}</style>
      </Card>
    </Col>
  );
}

export default CartItemCard;
